'use client';

/**
 * IntakeTemplateEditor - Template Editor Drawer Component
 *
 * Full-featured editor for intake flow templates.
 * Supports editing system templates (creates a copy) and custom templates.
 * Pure UI component - each portal provides its own save/delete handlers via props.
 *
 * Features:
 * - Step management (add, remove, reorder)
 * - Field management within steps (add, remove, reorder)
 * - Field options for select/radio/checkbox types
 * - Conditional display rules
 * - Rate calculator configuration
 * - Compliance questions management
 * - Icon and color pickers
 * - Real-time preview
 * - Validation before save
 *
 * Issue #432 - Templated Loan Intake Wizard
 */

import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AddIcon from '@mui/icons-material/Add';
import ColorIcon from '@mui/icons-material/ColorLens';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import HelpIcon from '@mui/icons-material/HelpOutlined';
import HistoryIcon from '@mui/icons-material/History';
import LanguageIcon from '@mui/icons-material/Language';
import MobileIcon from '@mui/icons-material/PhoneAndroid';
import PreviewIcon from '@mui/icons-material/Preview';
import RestoreIcon from '@mui/icons-material/Restore';
import SaveIcon from '@mui/icons-material/Save';
import SectionIcon from '@mui/icons-material/UnfoldMore';
import {
  Alert,
  Box,
  Chip,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Popover,
  Select,
  Slider,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  type IntakeComplianceQuestion,
  type IntakeCondition,
  type IntakeConditionGroup,
  type IntakeConditionOperator,
  IntakeDisplayMode,
  type IntakeFieldTemplate,
  type IntakeFieldType,
  type IntakeFlowTemplate,
  type IntakeFlowTemplateData,
  type IntakeFlowType,
  type IntakeFormTheme,
  type IntakeRateCalculatorConfig,
  type IntakeStepSection,
  type IntakeStepTemplate,
} from '@ops-matrix/shared-types';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { baseColors } from '../../theme/palette';
import { DetailPanel } from '../DetailPanel';
import { GlassButton } from '../GlassButton';
import { GlassTooltip } from '../GlassTooltip';
import { getI18nString } from '../IntakeWizard/intake-utils';
import { MagicInput } from '../MagicInput';
import { PanelSection } from '../SplitPanel/PanelSection';
import { SplitPanelContent } from '../SplitPanel/SplitPanelContent';
import { SplitPanelFooter } from '../SplitPanel/SplitPanelFooter';
import { SplitPanelHeader } from '../SplitPanel/SplitPanelHeader';

// =============================================================================
// TYPES
// =============================================================================

// Version info for template version history (P2 - Issue #471)
export interface TemplateVersionInfo {
  id: string;
  version: number;
  name: string;
  changelog?: string;
  status: 'draft' | 'published' | 'archived';
  createdAt: string;
  publishedAt?: string;
}

export interface IntakeTemplateEditorProps {
  /** Whether the drawer is open */
  open: boolean;
  /** Close handler */
  onClose: () => void;
  /** Template to edit (null for new) */
  template: IntakeFlowTemplate | null;
  /** Whether this is a system template (will create copy) */
  isSystemTemplate?: boolean;
  /** Save handler - called with editor data */
  onSave: (data: IntakeTemplateEditorData) => Promise<void>;
  /** Preview handler */
  onPreview?: (data: IntakeTemplateEditorData) => void;
  /** Whether save is in progress */
  isSaving?: boolean;
  /** Optional delete handler - if provided, shows delete button for custom templates */
  onDelete?: () => Promise<void>;
  /** Whether delete is in progress */
  isDeleting?: boolean;
  // Version management (P2 - Issue #471)
  /** Available versions for this template */
  versions?: TemplateVersionInfo[];
  /** Create new version handler */
  onCreateVersion?: (changelog: string) => Promise<void>;
  /** Restore version handler */
  onRestoreVersion?: (versionId: string) => Promise<void>;
  /** Publish version handler */
  onPublishVersion?: (versionId: string) => Promise<void>;
  /** Whether version operations are in progress */
  isVersionLoading?: boolean;
  // Multi-language support (P3 - Issue #471)
  /** Available languages */
  availableLanguages?: Array<{ code: string; name: string }>;
  /** Handle translation updates */
  onTranslationUpdate?: (fieldPath: string, languageCode: string, value: string) => void;
  // Mobile preview (P3 - Issue #471)
  /** Enable mobile preview mode */
  mobilePreviewEnabled?: boolean;
  /** Toggle mobile preview */
  onToggleMobilePreview?: (enabled: boolean) => void;
}

export interface IntakeTemplateEditorData {
  name: string;
  description: string;
  category: string;
  icon: string;
  color: string;
  tags: string[];
  data: IntakeFlowTemplateData;
  sourceTemplateId?: string;
  sourceTemplateName?: string;
}

// Field type options
const FIELD_TYPES: Array<{ value: IntakeFieldType; label: string; hasOptions?: boolean }> = [
  { value: 'text', label: 'Text' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'number', label: 'Number' },
  { value: 'currency', label: 'Currency' },
  { value: 'percentage', label: 'Percentage' },
  { value: 'select', label: 'Dropdown', hasOptions: true },
  { value: 'multi-select', label: 'Multi-Select', hasOptions: true },
  { value: 'radio', label: 'Radio Buttons', hasOptions: true },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'date', label: 'Date' },
  { value: 'address', label: 'Address' },
  { value: 'ssn', label: 'SSN (Masked)' },
];

// Intake type options
const INTAKE_TYPES: Array<{ value: IntakeFlowType; label: string }> = [
  { value: 'bridge', label: 'Bridge / Fix & Flip' },
  { value: 'construction', label: 'Construction' },
  { value: 'rental', label: 'Rental / DSCR' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'general', label: 'General Consultation' },
];

// Icon options for templates
const ICON_OPTIONS = [
  'Assignment',
  'Description',
  'AccountBalance',
  'Home',
  'Business',
  'AttachMoney',
  'TrendingUp',
  'Gavel',
  'Engineering',
  'Construction',
];

// Color options for templates (using design system colors).
// TODO(#1758 F2): default brand color falls back to blue; v3 DNA prefers mint — pending design decision on default vs. picker option.
const COLOR_OPTIONS = [
  baseColors.info[500], // Blue
  baseColors.success[500], // Green
  baseColors.warning[500], // Orange/Amber
  baseColors.primary[500], // Purple (brand)
  baseColors.error[400], // Pink/Rose
  baseColors.secondary[500], // Cyan
  baseColors.error[500], // Red
  baseColors.neutral[500], // Gray
  baseColors.teal[700], // Dark Teal
  baseColors.teal[500], // Teal
];

// Conditional display operators (must match IntakeConditionOperator type)
const CONDITION_OPERATORS: Array<{
  value: IntakeConditionOperator;
  label: string;
}> = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Does not equal' },
  { value: 'contains', label: 'Contains' },
  { value: 'not_contains', label: 'Does not contain' },
  { value: 'greater_than', label: 'Greater than' },
  { value: 'less_than', label: 'Less than' },
  { value: 'greater_than_or_equals', label: 'Greater than or equals' },
  { value: 'less_than_or_equals', label: 'Less than or equals' },
  { value: 'is_empty', label: 'Is empty' },
  { value: 'is_not_empty', label: 'Is not empty' },
  { value: 'in', label: 'In list' },
  { value: 'not_in', label: 'Not in list' },
  { value: 'starts_with', label: 'Starts with' },
  { value: 'ends_with', label: 'Ends with' },
];

// =============================================================================
// CONDITION BUILDER COMPONENT (P1 - Issue #471)
// =============================================================================

interface ConditionBuilderProps {
  condition: IntakeCondition | IntakeConditionGroup | undefined;
  onChange: (condition: IntakeCondition | IntakeConditionGroup | undefined) => void;
  allFieldNames: Array<{ stepName: string; fieldName: string; fieldLabel: string }>;
  excludeFieldName?: string;
  level?: number;
}

function ConditionBuilder({ condition, onChange, allFieldNames, excludeFieldName, level = 0 }: ConditionBuilderProps) {
  const isGroup = condition && 'logic' in condition;
  const isSimple = condition && 'field' in condition && !('logic' in condition);

  // Toggle between simple and advanced mode
  const handleModeChange = (useAdvanced: boolean) => {
    if (useAdvanced) {
      // Convert simple condition to group
      if (isSimple) {
        onChange({
          logic: 'AND',
          conditions: [condition as IntakeCondition],
        });
      } else {
        onChange({
          logic: 'AND',
          conditions: [],
        });
      }
    } else {
      // Convert group to simple condition (take first condition or reset)
      if (isGroup) {
        const group = condition as IntakeConditionGroup;
        const firstCondition = group.conditions.find((c) => 'field' in c) as IntakeCondition | undefined;
        onChange(firstCondition || { field: '', operator: 'equals', value: '' });
      } else {
        onChange({ field: '', operator: 'equals', value: '' });
      }
    }
  };

  // Handle group logic change
  const handleLogicChange = (logic: 'AND' | 'OR') => {
    if (isGroup) {
      onChange({ ...(condition as IntakeConditionGroup), logic });
    }
  };

  // Add condition to group
  const handleAddCondition = () => {
    if (isGroup) {
      const group = condition as IntakeConditionGroup;
      onChange({
        ...group,
        conditions: [...group.conditions, { field: '', operator: 'equals', value: '' }],
      });
    }
  };

  // Add nested group
  const handleAddNestedGroup = () => {
    if (isGroup) {
      const group = condition as IntakeConditionGroup;
      onChange({
        ...group,
        conditions: [...group.conditions, { logic: 'AND', conditions: [] }],
      });
    }
  };

  // Update condition in group
  const handleUpdateCondition = (index: number, newCondition: IntakeCondition | IntakeConditionGroup | undefined) => {
    if (isGroup && newCondition) {
      const group = condition as IntakeConditionGroup;
      const newConditions = [...group.conditions];
      newConditions[index] = newCondition;
      onChange({ ...group, conditions: newConditions });
    }
  };

  // Remove condition from group
  const handleRemoveCondition = (index: number) => {
    if (isGroup) {
      const group = condition as IntakeConditionGroup;
      onChange({
        ...group,
        conditions: group.conditions.filter((_, i) => i !== index),
      });
    }
  };

  // Update simple condition
  const handleSimpleConditionChange = (updates: Partial<IntakeCondition>) => {
    if (isSimple) {
      onChange({ ...(condition as IntakeCondition), ...updates });
    }
  };

  const filteredFieldNames = allFieldNames.filter((f) => f.fieldName !== excludeFieldName);

  return (
    <Box
      sx={{
        p: level > 0 ? 1.5 : 0,
        ml: level > 0 ? 2 : 0,
        borderLeft: level > 0 ? 2 : 0,
        borderColor: level > 0 ? 'primary.light' : 'transparent',
        bgcolor: level > 0 ? 'action.hover' : 'transparent',
        borderRadius: 1,
      }}
    >
      {level === 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Chip
            label="Simple"
            size="small"
            variant={!isGroup ? 'filled' : 'outlined'}
            color={!isGroup ? 'primary' : 'default'}
            onClick={() => handleModeChange(false)}
            sx={{ cursor: 'pointer' }}
          />
          <Chip
            label="Advanced (AND/OR)"
            size="small"
            variant={isGroup ? 'filled' : 'outlined'}
            color={isGroup ? 'primary' : 'default'}
            onClick={() => handleModeChange(true)}
            sx={{ cursor: 'pointer' }}
          />
        </Box>
      )}
      {isSimple && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>When field</InputLabel>
            <Select
              value={(condition as IntakeCondition).field}
              onChange={(e) => handleSimpleConditionChange({ field: e.target.value })}
              label="When field"
              MenuProps={{
                slotProps: {
                  backdrop: { invisible: true },
                },
              }}
            >
              {filteredFieldNames.map((f) => (
                <MenuItem key={f.fieldName} value={f.fieldName}>
                  {f.fieldLabel} ({f.stepName})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Operator</InputLabel>
            <Select
              value={(condition as IntakeCondition).operator}
              onChange={(e) => handleSimpleConditionChange({ operator: e.target.value as IntakeConditionOperator })}
              label="Operator"
              MenuProps={{
                slotProps: {
                  backdrop: { invisible: true },
                },
              }}
            >
              {CONDITION_OPERATORS.map((op) => (
                <MenuItem key={op.value} value={op.value}>
                  {op.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {!['is_empty', 'is_not_empty'].includes((condition as IntakeCondition).operator) && (
            <TextField
              size="small"
              label="Value"
              value={String((condition as IntakeCondition).value || '')}
              onChange={(e) => handleSimpleConditionChange({ value: e.target.value })}
              sx={{ minWidth: 100 }}
            />
          )}
        </Box>
      )}
      {isGroup && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              Match
            </Typography>
            <Chip
              label="ALL"
              size="small"
              variant={(condition as IntakeConditionGroup).logic === 'AND' ? 'filled' : 'outlined'}
              color={(condition as IntakeConditionGroup).logic === 'AND' ? 'primary' : 'default'}
              onClick={() => handleLogicChange('AND')}
              sx={{ cursor: 'pointer' }}
            />
            <Chip
              label="ANY"
              size="small"
              variant={(condition as IntakeConditionGroup).logic === 'OR' ? 'filled' : 'outlined'}
              color={(condition as IntakeConditionGroup).logic === 'OR' ? 'primary' : 'default'}
              onClick={() => handleLogicChange('OR')}
              sx={{ cursor: 'pointer' }}
            />
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              of the following:
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {(condition as IntakeConditionGroup).conditions.map((cond, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <ConditionBuilder
                    condition={cond as IntakeCondition | IntakeConditionGroup}
                    onChange={(newCond) => handleUpdateCondition(index, newCond)}
                    allFieldNames={filteredFieldNames}
                    excludeFieldName={excludeFieldName}
                    level={level + 1}
                  />
                </Box>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleRemoveCondition(index)}
                  sx={{ mt: level > 0 ? 0.5 : 0 }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <GlassButton variant="ghost" size="small" startIcon={<AddIcon />} onClick={handleAddCondition}>
              Add Condition
            </GlassButton>
            {level < 2 && (
              <GlassButton variant="ghost" size="small" startIcon={<AddIcon />} onClick={handleAddNestedGroup}>
                Add Group
              </GlassButton>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}

// =============================================================================
// SORTABLE STEP COMPONENT
// =============================================================================

interface SortableStepItemProps {
  step: IntakeStepTemplate;
  index: number;
  totalSteps: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onRemove: () => void;
  children?: React.ReactNode;
}

function SortableStepItem({
  step,
  index,
  totalSteps,
  isExpanded,
  onToggleExpand,
  onRemove,
  children,
}: SortableStepItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: step.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  return (
    <Box ref={setNodeRef} style={style}>
      <ListItem
        sx={{
          bgcolor: isExpanded ? 'action.selected' : isDragging ? 'action.hover' : 'transparent',
          borderRadius: 1,
          mb: 1,
          border: isDragging ? '2px dashed' : 'none',
          borderColor: 'primary.main',
        }}
      >
        <ListItemIcon sx={{ minWidth: 36, cursor: 'grab', touchAction: 'none' }} {...attributes} {...listeners}>
          <DragIcon sx={{ color: isDragging ? 'primary.main' : 'text.disabled' }} aria-hidden="true" />
        </ListItemIcon>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip label={index + 1} size="small" color="primary" />
              <Typography variant="subtitle2">{getI18nString(step.name)}</Typography>
              {step.requiresAuth && <Chip label="Auth Required" size="small" color="warning" variant="outlined" />}
            </Box>
          }
          secondary={`${step.fields.length} fields`}
        />
        <ListItemSecondaryAction>
          <GlassTooltip title="Edit step">
            <IconButton size="small" onClick={onToggleExpand} aria-label={isExpanded ? 'Collapse step' : 'Expand step'}>
              <EditIcon />
            </IconButton>
          </GlassTooltip>
          <GlassTooltip title="Delete step">
            <span>
              <IconButton
                size="small"
                onClick={onRemove}
                disabled={totalSteps <= 1}
                color="error"
                aria-label="Delete step"
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </GlassTooltip>
        </ListItemSecondaryAction>
      </ListItem>
      {children}
    </Box>
  );
}

// =============================================================================
// SORTABLE FIELD COMPONENT
// =============================================================================

interface SortableFieldItemProps {
  field: IntakeFieldTemplate;
  stepId: string;
  isEditing: boolean;
  onEdit: () => void;
  onRemove: () => void;
  onDoneEditing: () => void;
  onUpdateField: (updates: Partial<IntakeFieldTemplate>) => void;
  allFieldNames: Array<{ stepName: string; fieldName: string; fieldLabel: string }>;
  errors: Record<string, string | undefined>;
  onAddFieldOption: () => void;
  onUpdateFieldOption: (optionIndex: number, updates: { value?: string; label?: string }) => void;
  onRemoveFieldOption: (optionIndex: number) => void;
  onSetConditionalDisplay: (condition: IntakeCondition | IntakeConditionGroup | undefined) => void;
}

function SortableFieldItem({
  field,
  isEditing,
  onEdit,
  onRemove,
  onDoneEditing,
  onUpdateField,
  allFieldNames,
  errors,
  onAddFieldOption,
  onUpdateFieldOption,
  onRemoveFieldOption,
  onSetConditionalDisplay,
}: SortableFieldItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const fieldTypeHasOptions = (type: IntakeFieldType) => {
    return type === 'select' || type === 'radio';
  };

  if (isEditing) {
    // Field edit mode - not draggable
    return (
      <Box
        sx={{
          p: 1.5,
          bgcolor: 'action.selected',
          borderRadius: 1,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              label="Label"
              value={field.label}
              onChange={(e) => onUpdateField({ label: e.target.value })}
              size="small"
              sx={{ flex: 1 }}
              error={Boolean(errors[`field-${field.id}-label`])}
              aria-required="true"
            />
            <TextField
              label="Field Name"
              value={field.name}
              onChange={(e) =>
                onUpdateField({
                  name: e.target.value.replace(/\s/g, '_').toLowerCase(),
                })
              }
              size="small"
              sx={{ flex: 1 }}
              error={Boolean(errors[`field-${field.id}-name`])}
              aria-required="true"
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel id={`field-type-${field.id}`}>Type</InputLabel>
              <Select
                labelId={`field-type-${field.id}`}
                value={field.type}
                onChange={(e) =>
                  onUpdateField({
                    type: e.target.value as IntakeFieldType,
                    options: fieldTypeHasOptions(e.target.value as IntakeFieldType) ? field.options : undefined,
                  })
                }
                label="Type"
                MenuProps={{
                  slotProps: {
                    backdrop: { invisible: true },
                  },
                }}
              >
                {FIELD_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel id={`field-width-${field.id}`}>Width</InputLabel>
              <Select
                labelId={`field-width-${field.id}`}
                value={field.width || 12}
                onChange={(e) => onUpdateField({ width: e.target.value as number })}
                label="Width"
                MenuProps={{
                  slotProps: {
                    backdrop: { invisible: true },
                  },
                }}
              >
                <MenuItem value={3}>Quarter (25%)</MenuItem>
                <MenuItem value={4}>Third (33%)</MenuItem>
                <MenuItem value={6}>Half (50%)</MenuItem>
                <MenuItem value={8}>Two-Thirds (67%)</MenuItem>
                <MenuItem value={9}>Three-Quarters (75%)</MenuItem>
                <MenuItem value={12}>Full (100%)</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Help Text and Placeholder */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              label="Placeholder"
              value={field.placeholder || ''}
              onChange={(e) => onUpdateField({ placeholder: e.target.value })}
              size="small"
              sx={{ flex: 1 }}
            />
            <TextField
              label="Help Text"
              value={field.helpText || ''}
              onChange={(e) => onUpdateField({ helpText: e.target.value })}
              size="small"
              sx={{ flex: 1 }}
              slotProps={{
                input: {
                  endAdornment: (
                    <GlassTooltip title="Shows as helper text below the field">
                      <HelpIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </GlassTooltip>
                  ),
                },
              }}
            />
          </Box>

          {/* Validation & Sensitive Flag */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {['number', 'currency', 'percentage'].includes(field.type) && (
              <>
                <TextField
                  label="Min Value"
                  type="number"
                  value={field.validation?.min ?? ''}
                  onChange={(e) =>
                    onUpdateField({
                      validation: {
                        ...field.validation,
                        min: e.target.value ? Number(e.target.value) : undefined,
                      },
                    })
                  }
                  size="small"
                  sx={{ width: 100 }}
                />
                <TextField
                  label="Max Value"
                  type="number"
                  value={field.validation?.max ?? ''}
                  onChange={(e) =>
                    onUpdateField({
                      validation: {
                        ...field.validation,
                        max: e.target.value ? Number(e.target.value) : undefined,
                      },
                    })
                  }
                  size="small"
                  sx={{ width: 100 }}
                />
              </>
            )}
            {field.type === 'text' && (
              <TextField
                label="Pattern (regex)"
                value={field.validation?.pattern ?? ''}
                onChange={(e) =>
                  onUpdateField({
                    validation: {
                      ...field.validation,
                      pattern: e.target.value || undefined,
                    },
                  })
                }
                size="small"
                placeholder="e.g. ^[A-Z]{2}[0-9]{4}$"
                sx={{ minWidth: 200 }}
              />
            )}
            {(field.type === 'text' || ['number', 'currency', 'percentage'].includes(field.type)) && (
              <TextField
                label="Validation Message"
                value={field.validation?.message ?? ''}
                onChange={(e) =>
                  onUpdateField({
                    validation: {
                      ...field.validation,
                      message: e.target.value || undefined,
                    },
                  })
                }
                size="small"
                placeholder="Custom error message"
                sx={{ flex: 1, minWidth: 150 }}
              />
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={field.sensitive || false}
                  onChange={(e) => onUpdateField({ sensitive: e.target.checked })}
                  size="small"
                />
              }
              label={
                <GlassTooltip title="Mark fields containing PII (SSN, financial data) for audit logging">
                  <Typography variant="body2">Sensitive</Typography>
                </GlassTooltip>
              }
            />
          </Box>

          {/* Options for select/radio fields */}
          {fieldTypeHasOptions(field.type) && (
            <Box sx={{ mt: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  mb: 1,
                  display: 'block',
                }}
              >
                Options
              </Typography>
              {(field.options || []).map((option, optIndex) => (
                <Box key={optIndex} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Value"
                    value={option.value}
                    onChange={(e) => onUpdateFieldOption(optIndex, { value: e.target.value })}
                    sx={{ flex: 1 }}
                    aria-label={`Option ${optIndex + 1} value`}
                  />
                  <TextField
                    size="small"
                    placeholder="Label"
                    value={option.label}
                    onChange={(e) => onUpdateFieldOption(optIndex, { label: e.target.value })}
                    sx={{ flex: 1 }}
                    aria-label={`Option ${optIndex + 1} label`}
                  />
                  <IconButton
                    size="small"
                    onClick={() => onRemoveFieldOption(optIndex)}
                    color="error"
                    aria-label={`Remove option ${optIndex + 1}`}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
              <GlassButton variant="ghost" size="small" startIcon={<AddIcon />} onClick={onAddFieldOption}>
                Add Option
              </GlassButton>
            </Box>
          )}

          {/* Conditional Display - Enhanced with AND/OR (P1 - Issue #471) */}
          <Box sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(field.conditionalDisplay || field.conditionalDisplayGroup)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onSetConditionalDisplay({
                        field: '',
                        operator: 'equals',
                        value: '',
                      });
                    } else {
                      onSetConditionalDisplay(undefined);
                    }
                  }}
                  size="small"
                />
              }
              label="Conditional display"
            />
            {(field.conditionalDisplay || field.conditionalDisplayGroup) && (
              <Box sx={{ mt: 1, ml: 2 }}>
                <ConditionBuilder
                  condition={field.conditionalDisplayGroup || field.conditionalDisplay}
                  onChange={(newCondition) => onSetConditionalDisplay(newCondition)}
                  allFieldNames={allFieldNames}
                  excludeFieldName={field.name}
                />
              </Box>
            )}
          </Box>

          {/* Field Dependency - Show After Field (P1 - Issue #471) */}
          <Box sx={{ mt: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(field.showAfterField)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      // Default to first available field in this step
                      const availableFields = allFieldNames.filter((f) => f.fieldName !== field.name);
                      onUpdateField({ showAfterField: availableFields[0]?.fieldName || '' });
                    } else {
                      onUpdateField({ showAfterField: undefined });
                    }
                  }}
                  size="small"
                />
              }
              label="Show after specific field"
            />
            {field.showAfterField !== undefined && (
              <Box sx={{ mt: 1, ml: 4 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel id={`show-after-${field.id}`}>Show after</InputLabel>
                  <Select
                    labelId={`show-after-${field.id}`}
                    value={field.showAfterField || ''}
                    onChange={(e) => onUpdateField({ showAfterField: e.target.value })}
                    label="Show after"
                    MenuProps={{
                      slotProps: {
                        backdrop: { invisible: true },
                      },
                    }}
                  >
                    {allFieldNames
                      .filter((f) => f.fieldName !== field.name)
                      .map((f) => (
                        <MenuItem key={f.fieldName} value={f.fieldName}>
                          {f.fieldLabel} ({f.stepName})
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    mt: 0.5,
                    display: 'block',
                  }}
                >
                  This field will be positioned immediately after the selected field
                </Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={field.required}
                  onChange={(e) => onUpdateField({ required: e.target.checked })}
                  size="small"
                />
              }
              label="Required"
            />
            <Box sx={{ flex: 1 }} />
            <GlassButton variant="outlined" size="small" onClick={onDoneEditing}>
              Done
            </GlassButton>
          </Box>
        </Box>
      </Box>
    );
  }

  // Field display mode - draggable
  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        p: 1.5,
        bgcolor: isDragging ? 'action.selected' : 'action.hover',
        borderRadius: 1,
        border: isDragging ? '2px dashed' : 'none',
        borderColor: 'primary.main',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{ cursor: 'grab', touchAction: 'none', display: 'flex', alignItems: 'center' }}
          {...attributes}
          {...listeners}
        >
          <DragIcon sx={{ color: isDragging ? 'primary.main' : 'text.disabled' }} aria-hidden="true" />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
            }}
          >
            {getI18nString(field.label)}
            {field.required && (
              <Typography component="span" color="error" sx={{ ml: 0.5 }}>
                *
              </Typography>
            )}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            {field.name} · {FIELD_TYPES.find((t) => t.value === field.type)?.label || field.type}
            {field.conditionalDisplay && ' · Conditional'}
          </Typography>
        </Box>
        <GlassTooltip title="Edit field">
          <IconButton size="small" onClick={onEdit} aria-label="Edit field">
            <EditIcon fontSize="small" />
          </IconButton>
        </GlassTooltip>
        <GlassTooltip title="Delete field">
          <IconButton size="small" onClick={onRemove} color="error" aria-label="Delete field">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </GlassTooltip>
      </Box>
    </Box>
  );
}

// =============================================================================
// COMPONENT
// =============================================================================

export function IntakeTemplateEditor({
  open,
  onClose,
  template,
  isSystemTemplate = false,
  onSave,
  onPreview,
  isSaving = false,
  onDelete,
  isDeleting = false,
  // Version management (P2)
  versions = [],
  onCreateVersion,
  onRestoreVersion,
  onPublishVersion,
  isVersionLoading = false,
  // Multi-language support (P3)
  availableLanguages = [],
  onTranslationUpdate,
  // Mobile preview (P3)
  mobilePreviewEnabled = false,
  onToggleMobilePreview,
}: IntakeTemplateEditorProps) {
  const { t } = useI18n();

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Version panel state (P2)
  const [showVersionPanel, setShowVersionPanel] = useState(false);
  const [versionChangelog, setVersionChangelog] = useState('');

  // Translation panel state (P3)
  const [showTranslationPanel, setShowTranslationPanel] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('');

  // Form state - General
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [intakeType, setIntakeType] = useState<IntakeFlowType>('general');
  const [icon, setIcon] = useState('Assignment');
  const [color, setColor] = useState<string>(baseColors.info[500]);
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');

  // Form state - Steps
  const [steps, setSteps] = useState<IntakeStepTemplate[]>([]);
  const [exitBehavior, setExitBehavior] = useState<'case_creation' | 'advisor_redirect' | 'custom' | 'warn'>(
    'case_creation',
  );
  const [customExitUrl, setCustomExitUrl] = useState('');
  // Display mode: scroll (infinite scroll) is the default
  const [displayMode, setDisplayMode] = useState<IntakeDisplayMode>(IntakeDisplayMode.SCROLL);

  // Form state - Theme (P3 - Issue #471)
  const [formTheme, setFormTheme] = useState<IntakeFormTheme>({
    primaryColor: baseColors.info[500],
    secondaryColor: baseColors.warning[500],
    backgroundColor: baseColors.neutral[0],
    textColor: baseColors.neutral[900],
    fontFamily: 'Inter, sans-serif',
    borderRadius: 8,
    supportsDarkMode: true,
  });
  const [showThemePanel, setShowThemePanel] = useState(false);

  // Form state - Rate Calculator
  const [rateCalculatorEnabled, setRateCalculatorEnabled] = useState(false);
  const [rateCalculatorConfig, setRateCalculatorConfig] = useState<IntakeRateCalculatorConfig>({
    maxLTV: 75,
    maxLTC: 90,
    termOptions: [12, 24, 36],
    defaultRate: 10.5,
    minLoanAmount: 50000,
    maxLoanAmount: 10000000,
  });

  // Form state - Compliance Questions
  const [complianceQuestions, setComplianceQuestions] = useState<IntakeComplianceQuestion[]>([]);

  // UI state
  const [expandedStep, setExpandedStep] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<{ stepId: string; fieldId: string } | null>(null);
  const [colorAnchorEl, setColorAnchorEl] = useState<HTMLElement | null>(null);

  // Error state with typed keys
  interface ValidationErrors {
    name?: string;
    steps?: string;
    [key: `step-${string}-name`]: string | undefined;
    [key: `step-${string}-fields`]: string | undefined;
    [key: `field-${string}-label`]: string | undefined;
    [key: `field-${string}-name`]: string | undefined;
  }
  const [errors, setErrors] = useState<ValidationErrors>({});

  // Drag-and-drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Handle step drag end
  const handleStepDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        const oldIndex = steps.findIndex((s) => s.id === active.id);
        const newIndex = steps.findIndex((s) => s.id === over.id);
        const reordered = arrayMove(steps, oldIndex, newIndex).map((s, i) => ({ ...s, order: i }));
        setSteps(reordered);
      }
    },
    [steps],
  );

  // Handle field drag end within a step
  const handleFieldDragEnd = useCallback(
    (stepId: string, event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        setSteps(
          steps.map((s) => {
            if (s.id !== stepId) return s;
            const oldIndex = s.fields.findIndex((f) => f.id === active.id);
            const newIndex = s.fields.findIndex((f) => f.id === over.id);
            return { ...s, fields: arrayMove(s.fields, oldIndex, newIndex) };
          }),
        );
      }
    },
    [steps],
  );

  // Create a default step
  const createDefaultStep = useCallback(
    (order: number): IntakeStepTemplate => ({
      id: `step-${Date.now()}-${order}`,
      order,
      name: `Step ${order + 1}`,
      description: 'Enter step description',
      icon: 'Assignment',
      requiresAuth: false,
      fields: [],
    }),
    [],
  );

  // Initialize form when template changes
  useEffect(() => {
    if (template) {
      setName(isSystemTemplate ? `${template.name} (Custom)` : template.name);
      setDescription(template.description);
      setCategory(template.category);
      setIntakeType(template.data.intakeType);
      setIcon(template.icon || 'Assignment');
      setColor(template.color || baseColors.info[500]);
      setTags(template.tags || []);
      setSteps(JSON.parse(JSON.stringify(template.data.steps))); // Deep clone
      setExitBehavior(template.data.exitBehavior || 'case_creation');
      setCustomExitUrl(template.data.advisorRedirectUrl || '');
      setDisplayMode(template.data.displayMode || IntakeDisplayMode.SCROLL);
      setRateCalculatorEnabled(!!template.data.rateCalculatorConfig);
      setRateCalculatorConfig(
        template.data.rateCalculatorConfig || {
          maxLTV: 75,
          maxLTC: 90,
          termOptions: [12, 24, 36],
          defaultRate: 10.5,
          minLoanAmount: 50000,
          maxLoanAmount: 10000000,
        },
      );
      setComplianceQuestions(template.data.complianceQuestions || []);
    } else {
      // New template defaults
      setName('');
      setDescription('');
      setCategory('general');
      setIntakeType('general');
      setIcon('Assignment');
      setColor(baseColors.info[500]);
      setTags([]);
      setSteps([createDefaultStep(0)]);
      setExitBehavior('case_creation');
      setCustomExitUrl('');
      setRateCalculatorEnabled(false);
      setRateCalculatorConfig({
        maxLTV: 75,
        maxLTC: 90,
        termOptions: [12, 24, 36],
        defaultRate: 10.5,
        minLoanAmount: 50000,
        maxLoanAmount: 10000000,
      });
      setComplianceQuestions([]);
    }
    setActiveTab(0);
    setExpandedStep(null);
    setErrors({});
  }, [template, isSystemTemplate, open, createDefaultStep]);

  // Create a default field
  const createDefaultField = useCallback(
    (): IntakeFieldTemplate => ({
      id: `field-${Date.now()}`,
      name: `field_${Date.now()}`,
      label: 'New Field',
      type: 'text',
      required: false,
      width: 12,
      helpText: '',
      placeholder: '',
    }),
    [],
  );

  // Step handlers
  const handleAddStep = useCallback(() => {
    const newStep = createDefaultStep(steps.length);
    setSteps([...steps, newStep]);
    setExpandedStep(newStep.id);
  }, [steps, createDefaultStep]);

  const handleRemoveStep = useCallback(
    (stepId: string) => {
      setSteps(steps.filter((s) => s.id !== stepId).map((s, i) => ({ ...s, order: i })));
      if (expandedStep === stepId) {
        setExpandedStep(null);
      }
    },
    [steps, expandedStep],
  );

  const handleUpdateStep = useCallback(
    (stepId: string, updates: Partial<IntakeStepTemplate>) => {
      setSteps(steps.map((s) => (s.id === stepId ? { ...s, ...updates } : s)));
    },
    [steps],
  );

  // Field handlers
  const handleAddField = useCallback(
    (stepId: string) => {
      const newField = createDefaultField();
      setSteps(steps.map((s) => (s.id === stepId ? { ...s, fields: [...s.fields, newField] } : s)));
      setEditingField({ stepId, fieldId: newField.id });
    },
    [steps, createDefaultField],
  );

  const handleRemoveField = useCallback(
    (stepId: string, fieldId: string) => {
      setSteps(steps.map((s) => (s.id === stepId ? { ...s, fields: s.fields.filter((f) => f.id !== fieldId) } : s)));
      if (editingField?.fieldId === fieldId) {
        setEditingField(null);
      }
    },
    [steps, editingField],
  );

  const handleUpdateField = useCallback(
    (stepId: string, fieldId: string, updates: Partial<IntakeFieldTemplate>) => {
      setSteps(
        steps.map((s) =>
          s.id === stepId
            ? {
                ...s,
                fields: s.fields.map((f) => (f.id === fieldId ? { ...f, ...updates } : f)),
              }
            : s,
        ),
      );
    },
    [steps],
  );

  // Field options handlers (for select, radio types)
  const handleAddFieldOption = useCallback(
    (stepId: string, fieldId: string) => {
      const step = steps.find((s) => s.id === stepId);
      const field = step?.fields.find((f) => f.id === fieldId);
      if (!field) return;

      const options = field.options || [];
      const newOption = { value: `option_${options.length + 1}`, label: `Option ${options.length + 1}` };
      handleUpdateField(stepId, fieldId, { options: [...options, newOption] });
    },
    [steps, handleUpdateField],
  );

  const handleUpdateFieldOption = useCallback(
    (stepId: string, fieldId: string, optionIndex: number, updates: { value?: string; label?: string }) => {
      const step = steps.find((s) => s.id === stepId);
      const field = step?.fields.find((f) => f.id === fieldId);
      if (!field?.options) return;

      const newOptions = [...field.options];
      newOptions[optionIndex] = { ...newOptions[optionIndex], ...updates };
      handleUpdateField(stepId, fieldId, { options: newOptions });
    },
    [steps, handleUpdateField],
  );

  const handleRemoveFieldOption = useCallback(
    (stepId: string, fieldId: string, optionIndex: number) => {
      const step = steps.find((s) => s.id === stepId);
      const field = step?.fields.find((f) => f.id === fieldId);
      if (!field?.options) return;

      const newOptions = field.options.filter((_, i) => i !== optionIndex);
      handleUpdateField(stepId, fieldId, { options: newOptions });
    },
    [steps, handleUpdateField],
  );

  // Conditional display handlers - supports both simple and advanced (AND/OR) conditions
  const handleSetConditionalDisplay = useCallback(
    (stepId: string, fieldId: string, condition: IntakeCondition | IntakeConditionGroup | undefined) => {
      // Determine if it's a condition group (has 'logic' property) or simple condition
      if (!condition) {
        // Clear both types
        handleUpdateField(stepId, fieldId, { conditionalDisplay: undefined, conditionalDisplayGroup: undefined });
      } else if ('logic' in condition) {
        // It's a condition group
        handleUpdateField(stepId, fieldId, { conditionalDisplay: undefined, conditionalDisplayGroup: condition });
      } else {
        // It's a simple condition
        handleUpdateField(stepId, fieldId, { conditionalDisplay: condition, conditionalDisplayGroup: undefined });
      }
    },
    [handleUpdateField],
  );

  // Section handlers
  const handleAddSection = useCallback(
    (stepId: string) => {
      const step = steps.find((s) => s.id === stepId);
      if (!step) return;

      const newSection: IntakeStepSection = {
        id: `section-${Date.now()}`,
        title: `Section ${(step.sections?.length || 0) + 1}`,
        collapsible: true,
        collapsed: false,
        fieldIds: [],
      };

      setSteps(steps.map((s) => (s.id === stepId ? { ...s, sections: [...(s.sections || []), newSection] } : s)));
    },
    [steps],
  );

  const handleUpdateSection = useCallback(
    (stepId: string, sectionId: string, updates: Partial<IntakeStepSection>) => {
      setSteps(
        steps.map((s) =>
          s.id === stepId
            ? {
                ...s,
                sections: s.sections?.map((sec) => (sec.id === sectionId ? { ...sec, ...updates } : sec)),
              }
            : s,
        ),
      );
    },
    [steps],
  );

  const handleRemoveSection = useCallback(
    (stepId: string, sectionId: string) => {
      setSteps(
        steps.map((s) =>
          s.id === stepId
            ? {
                ...s,
                sections: s.sections?.filter((sec) => sec.id !== sectionId),
              }
            : s,
        ),
      );
    },
    [steps],
  );

  const handleToggleFieldInSection = useCallback(
    (stepId: string, sectionId: string, fieldId: string) => {
      setSteps(
        steps.map((s) => {
          if (s.id !== stepId) return s;
          return {
            ...s,
            sections: s.sections?.map((sec) => {
              if (sec.id !== sectionId) {
                // Remove field from other sections if it's being added to this one
                const otherFieldIds = sec.fieldIds ? sec.fieldIds.filter((fId) => fId !== fieldId) : [];
                return {
                  ...sec,
                  fieldIds: otherFieldIds,
                };
              }
              // Toggle field in this section
              const currentFieldIds = sec.fieldIds ?? [];
              const isInSection = currentFieldIds.includes(fieldId);
              const newFieldIds = isInSection
                ? currentFieldIds.filter((fId) => fId !== fieldId)
                : [...currentFieldIds, fieldId];
              return {
                ...sec,
                fieldIds: newFieldIds,
              };
            }),
          };
        }),
      );
    },
    [steps],
  );

  // Tag handlers
  const handleAddTag = useCallback(() => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  }, [tagInput, tags]);

  const handleRemoveTag = useCallback(
    (tag: string) => {
      setTags(tags.filter((t) => t !== tag));
    },
    [tags],
  );

  // Compliance question handlers
  const handleAddComplianceQuestion = useCallback(() => {
    const newQuestion: IntakeComplianceQuestion = {
      id: `compliance-${Date.now()}`,
      question: 'New compliance question',
      requiredAnswer: true,
      blockingMessage: 'You must acknowledge this to proceed.',
    };
    setComplianceQuestions([...complianceQuestions, newQuestion]);
  }, [complianceQuestions]);

  const handleUpdateComplianceQuestion = useCallback(
    (questionId: string, updates: Partial<IntakeComplianceQuestion>) => {
      setComplianceQuestions(complianceQuestions.map((q) => (q.id === questionId ? { ...q, ...updates } : q)));
    },
    [complianceQuestions],
  );

  const handleRemoveComplianceQuestion = useCallback(
    (questionId: string) => {
      setComplianceQuestions(complianceQuestions.filter((q) => q.id !== questionId));
    },
    [complianceQuestions],
  );

  // Validation
  const validate = useCallback((): boolean => {
    const newErrors: ValidationErrors = {};

    if (!name.trim()) {
      newErrors['name'] = 'Template name is required';
    }

    if (steps.length === 0) {
      newErrors['steps'] = 'At least one step is required';
    }

    for (const step of steps) {
      if (!getI18nString(step.name).trim()) {
        newErrors[`step-${step.id}-name`] = 'Step name is required';
      }
      if (step.fields.length === 0) {
        newErrors[`step-${step.id}-fields`] = 'Each step must have at least one field';
      }
      for (const field of step.fields) {
        if (!getI18nString(field.label).trim()) {
          newErrors[`field-${field.id}-label`] = 'Field label is required';
        }
        if (!field.name.trim()) {
          newErrors[`field-${field.id}-name`] = 'Field name is required';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [name, steps]);

  // Build editor data
  const buildEditorData = useCallback((): IntakeTemplateEditorData => {
    return {
      name,
      description,
      category,
      icon,
      color,
      tags,
      data: {
        intakeType,
        version: '1.0.0',
        steps,
        complianceQuestions,
        exitBehavior,
        displayMode,
        rateCalculatorConfig: rateCalculatorEnabled ? rateCalculatorConfig : undefined,
        advisorRedirectUrl: exitBehavior === 'custom' ? customExitUrl : undefined,
      },
      sourceTemplateId: isSystemTemplate ? template?.id : undefined,
      sourceTemplateName: isSystemTemplate ? template?.name : undefined,
    };
  }, [
    name,
    description,
    category,
    icon,
    color,
    tags,
    intakeType,
    steps,
    complianceQuestions,
    exitBehavior,
    displayMode,
    customExitUrl,
    rateCalculatorEnabled,
    rateCalculatorConfig,
    template,
    isSystemTemplate,
  ]);

  // Save handler
  const handleSave = useCallback(async () => {
    if (!validate()) return;
    await onSave(buildEditorData());
  }, [validate, onSave, buildEditorData]);

  // Preview handler
  const handlePreview = useCallback(() => {
    if (onPreview) {
      onPreview(buildEditorData());
    }
  }, [onPreview, buildEditorData]);

  // Stats
  const totalFields = useMemo(() => steps.reduce((acc, s) => acc + s.fields.length, 0), [steps]);
  const requiredFields = useMemo(
    () => steps.reduce((acc, s) => acc + s.fields.filter((f) => f.required).length, 0),
    [steps],
  );

  // Get all field names for conditional display dropdown
  const allFieldNames = useMemo(() => {
    const names: Array<{ stepName: string; fieldName: string; fieldLabel: string }> = [];
    for (const step of steps) {
      for (const field of step.fields) {
        names.push({
          stepName: getI18nString(step.name),
          fieldName: field.name,
          fieldLabel: getI18nString(field.label),
        });
      }
    }
    return names;
  }, [steps]);

  // Can delete (custom templates only, when handler provided)
  const canDelete = !isSystemTemplate && onDelete && template;

  return (
    <DetailPanel open={open} onClose={onClose} width={650} anchor="right">
      <SplitPanelHeader
        title={template ? (isSystemTemplate ? 'Customize Template' : 'Edit Template') : 'Create Template'}
        subtitle={template?.name}
        onClose={onClose}
      />
      <SplitPanelContent>
        {/* System template warning */}
        {isSystemTemplate && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You're customizing a system template. A copy will be created for your business.
          </Alert>
        )}

        {/* Validation errors */}
        {Object.keys(errors).length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Please fix the errors below before saving.
          </Alert>
        )}

        {/* Toolbar - Version, Translation, Mobile Preview (P2/P3 - Issue #471) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {/* Version History Button */}
          {onCreateVersion && (
            <GlassTooltip title="Version History">
              <Chip
                icon={<HistoryIcon />}
                label={versions.length > 0 ? `v${versions[0]?.version || 1}` : 'v1'}
                size="small"
                variant={showVersionPanel ? 'filled' : 'outlined'}
                onClick={() => setShowVersionPanel(!showVersionPanel)}
                sx={{ cursor: 'pointer' }}
              />
            </GlassTooltip>
          )}

          {/* Translation Button */}
          {availableLanguages.length > 0 && onTranslationUpdate && (
            <GlassTooltip title="Translations">
              <Chip
                icon={<LanguageIcon />}
                label={`${availableLanguages.length} languages`}
                size="small"
                variant={showTranslationPanel ? 'filled' : 'outlined'}
                onClick={() => setShowTranslationPanel(!showTranslationPanel)}
                sx={{ cursor: 'pointer' }}
              />
            </GlassTooltip>
          )}

          {/* Mobile Preview Toggle */}
          {onToggleMobilePreview && (
            <GlassTooltip title={mobilePreviewEnabled ? 'Disable Mobile Preview' : 'Enable Mobile Preview'}>
              <Chip
                icon={<MobileIcon />}
                label="Mobile"
                size="small"
                variant={mobilePreviewEnabled ? 'filled' : 'outlined'}
                color={mobilePreviewEnabled ? 'primary' : 'default'}
                onClick={() => onToggleMobilePreview(!mobilePreviewEnabled)}
                sx={{ cursor: 'pointer' }}
              />
            </GlassTooltip>
          )}

          <Box sx={{ flex: 1 }} />

          {/* Preview Button */}
          {onPreview && (
            <GlassTooltip title="Preview Form">
              <IconButton size="small" onClick={handlePreview}>
                <PreviewIcon />
              </IconButton>
            </GlassTooltip>
          )}
        </Box>

        {/* Version History Panel (P2 - Issue #471) */}
        <Collapse in={showVersionPanel}>
          <Box
            sx={{
              p: 2,
              mb: 2,
              bgcolor: 'action.hover',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon fontSize="small" />
              Version History
            </Typography>

            {/* Create New Version */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                size="small"
                placeholder="Version changelog (optional)"
                value={versionChangelog}
                onChange={(e) => setVersionChangelog(e.target.value)}
                sx={{ flex: 1 }}
              />
              <GlassButton
                variant="gradient"
                size="small"
                onClick={async () => {
                  if (onCreateVersion) {
                    await onCreateVersion(versionChangelog);
                    setVersionChangelog('');
                  }
                }}
                loading={isVersionLoading}
              >
                Save Version
              </GlassButton>
            </Box>

            {/* Version List */}
            {versions.length > 0 ? (
              <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                {versions.map((version) => (
                  <ListItem
                    key={version.id}
                    sx={{
                      bgcolor: 'background.paper',
                      borderRadius: 1,
                      mb: 0.5,
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                            }}
                          >
                            v{version.version}
                          </Typography>
                          <Chip
                            label={version.status}
                            size="small"
                            color={
                              version.status === 'published'
                                ? 'success'
                                : version.status === 'draft'
                                  ? 'warning'
                                  : 'default'
                            }
                            sx={{ height: 20 }}
                          />
                        </Box>
                      }
                      secondary={
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                          }}
                        >
                          {version.changelog || 'No changelog'} • {new Date(version.createdAt).toLocaleDateString()}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      {version.status === 'draft' && onPublishVersion && (
                        <GlassTooltip title="Publish">
                          <IconButton
                            size="small"
                            onClick={() => onPublishVersion(version.id)}
                            disabled={isVersionLoading}
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                        </GlassTooltip>
                      )}
                      {onRestoreVersion && (
                        <GlassTooltip title="Restore this version">
                          <IconButton
                            size="small"
                            onClick={() => onRestoreVersion(version.id)}
                            disabled={isVersionLoading}
                          >
                            <RestoreIcon fontSize="small" />
                          </IconButton>
                        </GlassTooltip>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                No versions saved yet
              </Typography>
            )}
          </Box>
        </Collapse>

        {/* Translation Panel (P3 - Issue #471) */}
        <Collapse in={showTranslationPanel}>
          <Box
            sx={{
              p: 2,
              mb: 2,
              bgcolor: 'action.hover',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <LanguageIcon fontSize="small" />
              Multi-Language Support
            </Typography>

            <FormControl size="small" fullWidth sx={{ mb: 2 }}>
              <InputLabel>Select Language</InputLabel>
              <Select
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                label="Select Language"
                MenuProps={{
                  slotProps: {
                    backdrop: { invisible: true },
                  },
                }}
              >
                {availableLanguages.map((lang) => (
                  <MenuItem key={lang.code} value={lang.code}>
                    {lang.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedLanguage && (
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Select a field below and edit its translation in the selected language. Translations are saved
                automatically when you save the template.
              </Typography>
            )}
          </Box>
        </Collapse>

        {/* Tabs */}
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }} aria-label="Template editor tabs">
          <Tab label="General" id="tab-general" aria-controls="tabpanel-general" />
          <Tab label={`Steps (${steps.length})`} id="tab-steps" aria-controls="tabpanel-steps" />
          <Tab label="Settings" id="tab-settings" aria-controls="tabpanel-settings" />
          <Tab label="Compliance" id="tab-compliance" aria-controls="tabpanel-compliance" />
        </Tabs>

        {/* General Tab */}
        {activeTab === 0 && (
          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            role="tabpanel"
            id="tabpanel-general"
            aria-labelledby="tab-general"
          >
            <MagicInput
              label={t('intake.editor.templateName') || 'Template Name'}
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={Boolean(errors['name'])}
              helperText={errors['name']}
              required
              color="primary"
              aria-required="true"
            />

            <MagicInput
              label={t('intake.editor.description') || 'Description'}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={3}
              color="primary"
            />

            <FormControl fullWidth>
              <InputLabel id="intake-type-label">{t('intake.editor.intakeType') || 'Intake Type'}</InputLabel>
              <Select
                labelId="intake-type-label"
                value={intakeType}
                onChange={(e) => setIntakeType(e.target.value as IntakeFlowType)}
                label="Intake Type"
                MenuProps={{
                  slotProps: {
                    backdrop: { invisible: true },
                  },
                }}
              >
                {INTAKE_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Icon and Color Pickers */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl sx={{ flex: 1 }}>
                <InputLabel id="icon-select-label">Icon</InputLabel>
                <Select
                  labelId="icon-select-label"
                  value={icon}
                  onChange={(e) => setIcon(e.target.value)}
                  label="Icon"
                  MenuProps={{
                    slotProps: {
                      backdrop: { invisible: true },
                    },
                  }}
                >
                  {ICON_OPTIONS.map((iconName) => (
                    <MenuItem key={iconName} value={iconName}>
                      {iconName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    mb: 0.5,
                    display: 'block',
                  }}
                >
                  Color
                </Typography>
                <Box
                  onClick={(e) => setColorAnchorEl(e.currentTarget)}
                  sx={{
                    width: '100%',
                    height: 40,
                    borderRadius: 1,
                    bgcolor: color,
                    cursor: 'pointer',
                    border: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': { opacity: 0.8 },
                  }}
                  role="button"
                  aria-label="Select color"
                  tabIndex={0}
                >
                  <ColorIcon sx={{ color: '#fff' }} />
                </Box>
                <Popover
                  open={Boolean(colorAnchorEl)}
                  anchorEl={colorAnchorEl}
                  onClose={() => setColorAnchorEl(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                >
                  <Box sx={{ p: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1, maxWidth: 200 }}>
                    {COLOR_OPTIONS.map((c) => (
                      <Box
                        key={c}
                        onClick={() => {
                          setColor(c);
                          setColorAnchorEl(null);
                        }}
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1,
                          bgcolor: c,
                          cursor: 'pointer',
                          border: color === c ? 2 : 1,
                          borderColor: color === c ? 'primary.main' : 'divider',
                          '&:hover': { transform: 'scale(1.1)' },
                        }}
                        role="button"
                        aria-label={`Select color ${c}`}
                        tabIndex={0}
                      />
                    ))}
                  </Box>
                </Popover>
              </Box>
            </Box>

            {/* Tags */}
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  mb: 0.5,
                  display: 'block',
                }}
              >
                Tags
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                {tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    onDelete={() => handleRemoveTag(tag)}
                    aria-label={`Remove tag ${tag}`}
                  />
                ))}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small"
                  placeholder="Add tag..."
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                  sx={{ flex: 1 }}
                  aria-label="New tag input"
                />
                <GlassButton variant="outlined" size="small" onClick={handleAddTag}>
                  Add
                </GlassButton>
              </Box>
            </Box>

            {/* Stats */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Chip label={`${steps.length} steps`} size="small" />
              <Chip label={`${totalFields} fields`} size="small" />
              <Chip label={`${requiredFields} required`} size="small" color="warning" />
            </Box>
          </Box>
        )}

        {/* Steps Tab */}
        {activeTab === 1 && (
          <Box role="tabpanel" id="tabpanel-steps" aria-labelledby="tab-steps">
            {errors['steps'] && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors['steps']}
              </Alert>
            )}

            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
                mb: 1,
              }}
            >
              Drag steps to reorder. Click edit to modify step details and fields.
            </Typography>

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleStepDragEnd}>
              <SortableContext items={steps.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                <List sx={{ '& .MuiListItem-root': { px: 0 } }}>
                  {steps.map((step, index) => (
                    <SortableStepItem
                      key={step.id}
                      step={step}
                      index={index}
                      totalSteps={steps.length}
                      isExpanded={expandedStep === step.id}
                      onToggleExpand={() => setExpandedStep(expandedStep === step.id ? null : step.id)}
                      onRemove={() => handleRemoveStep(step.id)}
                    >
                      {/* Expanded step editor */}
                      <Collapse in={expandedStep === step.id}>
                        <Box
                          sx={{
                            ml: 5,
                            mb: 2,
                            p: 2,
                            bgcolor: 'background.paper',
                            borderRadius: 1,
                            border: 1,
                            borderColor: 'divider',
                          }}
                        >
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                              label="Step Name"
                              value={step.name}
                              onChange={(e) => handleUpdateStep(step.id, { name: e.target.value })}
                              size="small"
                              error={Boolean(errors[`step-${step.id}-name`])}
                              helperText={errors[`step-${step.id}-name`]}
                              aria-required="true"
                            />
                            <TextField
                              label="Description"
                              value={step.description}
                              onChange={(e) => handleUpdateStep(step.id, { description: e.target.value })}
                              size="small"
                              multiline
                              rows={2}
                            />
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                              <FormControl size="small" sx={{ minWidth: 150 }}>
                                <InputLabel id={`step-icon-${step.id}`}>Step Icon</InputLabel>
                                <Select
                                  labelId={`step-icon-${step.id}`}
                                  value={step.icon || 'Assignment'}
                                  onChange={(e) => handleUpdateStep(step.id, { icon: e.target.value })}
                                  label="Step Icon"
                                  MenuProps={{
                                    slotProps: {
                                      backdrop: { invisible: true },
                                    },
                                  }}
                                >
                                  {ICON_OPTIONS.map((iconName) => (
                                    <MenuItem key={iconName} value={iconName}>
                                      {iconName}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={step.requiresAuth}
                                    onChange={(e) => handleUpdateStep(step.id, { requiresAuth: e.target.checked })}
                                  />
                                }
                                label="Requires authentication"
                              />
                            </Box>

                            {/* Step-level Conditional Display */}
                            <Box sx={{ mt: 1 }}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={Boolean(step.conditionalDisplay)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        handleUpdateStep(step.id, {
                                          conditionalDisplay: { field: '', operator: 'equals', value: '' },
                                        });
                                      } else {
                                        handleUpdateStep(step.id, { conditionalDisplay: undefined });
                                      }
                                    }}
                                    size="small"
                                  />
                                }
                                label="Show step conditionally"
                              />
                              {step.conditionalDisplay && (
                                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                                  <TextField
                                    size="small"
                                    label="Field name"
                                    value={step.conditionalDisplay.field}
                                    onChange={(e) =>
                                      handleUpdateStep(step.id, {
                                        conditionalDisplay: { ...step.conditionalDisplay!, field: e.target.value },
                                      })
                                    }
                                    placeholder="e.g. loanPurpose"
                                    sx={{ minWidth: 150 }}
                                  />
                                  <FormControl size="small" sx={{ minWidth: 120 }}>
                                    <InputLabel>Operator</InputLabel>
                                    <Select
                                      value={step.conditionalDisplay.operator}
                                      onChange={(e) =>
                                        handleUpdateStep(step.id, {
                                          conditionalDisplay: { ...step.conditionalDisplay!, operator: e.target.value },
                                        })
                                      }
                                      label="Operator"
                                      MenuProps={{
                                        slotProps: {
                                          backdrop: { invisible: true },
                                        },
                                      }}
                                    >
                                      {CONDITION_OPERATORS.map((op) => (
                                        <MenuItem key={op.value} value={op.value}>
                                          {op.label}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                  <TextField
                                    size="small"
                                    label="Value"
                                    value={step.conditionalDisplay.value}
                                    onChange={(e) =>
                                      handleUpdateStep(step.id, {
                                        conditionalDisplay: { ...step.conditionalDisplay!, value: e.target.value },
                                      })
                                    }
                                    sx={{ minWidth: 120 }}
                                  />
                                </Box>
                              )}
                            </Box>

                            <Divider sx={{ my: 1 }} />

                            {/* Fields */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="subtitle2">Fields (drag to reorder)</Typography>
                              <GlassButton
                                variant="outlined"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => handleAddField(step.id)}
                              >
                                Add Field
                              </GlassButton>
                            </Box>

                            {errors[`step-${step.id}-fields`] && (
                              <Alert severity="error" variant="outlined">
                                {errors[`step-${step.id}-fields`]}
                              </Alert>
                            )}

                            {/* Sortable fields within step */}
                            <DndContext
                              sensors={sensors}
                              collisionDetection={closestCenter}
                              onDragEnd={(event) => handleFieldDragEnd(step.id, event)}
                            >
                              <SortableContext
                                items={step.fields.map((f) => f.id)}
                                strategy={verticalListSortingStrategy}
                              >
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                  {step.fields.map((field) => (
                                    <SortableFieldItem
                                      key={field.id}
                                      field={field}
                                      stepId={step.id}
                                      isEditing={editingField?.fieldId === field.id}
                                      onEdit={() => setEditingField({ stepId: step.id, fieldId: field.id })}
                                      onRemove={() => handleRemoveField(step.id, field.id)}
                                      onDoneEditing={() => setEditingField(null)}
                                      onUpdateField={(updates) => handleUpdateField(step.id, field.id, updates)}
                                      allFieldNames={allFieldNames}
                                      errors={errors as Record<string, string | undefined>}
                                      onAddFieldOption={() => handleAddFieldOption(step.id, field.id)}
                                      onUpdateFieldOption={(optIndex, updates) =>
                                        handleUpdateFieldOption(step.id, field.id, optIndex, updates)
                                      }
                                      onRemoveFieldOption={(optIndex) =>
                                        handleRemoveFieldOption(step.id, field.id, optIndex)
                                      }
                                      onSetConditionalDisplay={(condition) =>
                                        handleSetConditionalDisplay(step.id, field.id, condition)
                                      }
                                    />
                                  ))}
                                </Box>
                              </SortableContext>
                            </DndContext>

                            <Divider sx={{ my: 2 }} />

                            {/* Sections */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <SectionIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                <Typography variant="subtitle2">Sections ({step.sections?.length || 0})</Typography>
                              </Box>
                              <GlassButton
                                variant="outlined"
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={() => handleAddSection(step.id)}
                              >
                                Add Section
                              </GlassButton>
                            </Box>

                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                display: 'block',
                                mb: 1,
                              }}
                            >
                              Group fields into collapsible sections for better organization.
                            </Typography>

                            {step.sections && step.sections.length > 0 ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {step.sections.map((section) => (
                                  <Box
                                    key={section.id}
                                    sx={{
                                      p: 1.5,
                                      bgcolor: 'action.hover',
                                      borderRadius: 1,
                                      border: 1,
                                      borderColor: 'divider',
                                    }}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                      <TextField
                                        size="small"
                                        label="Section Title"
                                        value={getI18nString(section.title)}
                                        onChange={(e) =>
                                          handleUpdateSection(step.id, section.id, { title: e.target.value })
                                        }
                                        sx={{ flex: 1 }}
                                      />
                                      <FormControlLabel
                                        control={
                                          <Switch
                                            checked={section.collapsible || false}
                                            onChange={(e) =>
                                              handleUpdateSection(step.id, section.id, {
                                                collapsible: e.target.checked,
                                              })
                                            }
                                            size="small"
                                          />
                                        }
                                        label="Collapsible"
                                      />
                                      <GlassTooltip title="Delete section">
                                        <IconButton
                                          size="small"
                                          color="error"
                                          onClick={() => handleRemoveSection(step.id, section.id)}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </GlassTooltip>
                                    </Box>

                                    {/* Field assignment */}
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: 'text.secondary',
                                        display: 'block',
                                        mb: 0.5,
                                      }}
                                    >
                                      Fields in this section:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                      {step.fields.map((field) => {
                                        const isInSection = section.fieldIds?.includes(field.id) ?? false;
                                        return (
                                          <Chip
                                            key={field.id}
                                            label={getI18nString(field.label)}
                                            size="small"
                                            variant={isInSection ? 'filled' : 'outlined'}
                                            color={isInSection ? 'primary' : 'default'}
                                            onClick={() => handleToggleFieldInSection(step.id, section.id, field.id)}
                                            sx={{ cursor: 'pointer' }}
                                          />
                                        );
                                      })}
                                    </Box>
                                    {(section.fieldIds?.length ?? 0) === 0 && (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: 'text.disabled',
                                          fontStyle: 'italic',
                                        }}
                                      >
                                        Click fields above to add them to this section
                                      </Typography>
                                    )}
                                  </Box>
                                ))}
                              </Box>
                            ) : (
                              <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
                                No sections defined. All fields will display without grouping.
                              </Alert>
                            )}
                          </Box>
                        </Box>
                      </Collapse>
                    </SortableStepItem>
                  ))}
                </List>
              </SortableContext>
            </DndContext>

            <GlassButton variant="outlined" startIcon={<AddIcon />} onClick={handleAddStep} fullWidth sx={{ mt: 2 }}>
              Add Step
            </GlassButton>
          </Box>
        )}

        {/* Settings Tab */}
        {activeTab === 2 && (
          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            role="tabpanel"
            id="tabpanel-settings"
            aria-labelledby="tab-settings"
          >
            <PanelSection title="Display Mode">
              <FormControl fullWidth>
                <InputLabel id="display-mode-label">Form Layout</InputLabel>
                <Select
                  labelId="display-mode-label"
                  value={displayMode}
                  onChange={(e) => setDisplayMode(e.target.value as IntakeDisplayMode)}
                  label="Form Layout"
                  MenuProps={{
                    slotProps: {
                      backdrop: { invisible: true },
                    },
                  }}
                >
                  <MenuItem value={IntakeDisplayMode.SCROLL}>Infinite Scroll (all sections visible)</MenuItem>
                  <MenuItem value={IntakeDisplayMode.WIZARD}>Step-by-Step Wizard (one section at a time)</MenuItem>
                </Select>
              </FormControl>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  mt: 1,
                }}
              >
                {displayMode === IntakeDisplayMode.SCROLL
                  ? 'All sections are shown at once. The sidebar highlights the current section as you scroll.'
                  : 'Users navigate through sections one at a time using Previous/Next buttons.'}
              </Typography>
            </PanelSection>

            <PanelSection title="Completion Behavior">
              <FormControl fullWidth>
                <InputLabel id="exit-behavior-label">After completion</InputLabel>
                <Select
                  labelId="exit-behavior-label"
                  value={exitBehavior}
                  onChange={(e) => setExitBehavior(e.target.value as typeof exitBehavior)}
                  label="After completion"
                  MenuProps={{
                    slotProps: {
                      backdrop: { invisible: true },
                    },
                  }}
                >
                  <MenuItem value="case_creation">Create case automatically</MenuItem>
                  <MenuItem value="advisor_redirect">Redirect to advisor (lead capture)</MenuItem>
                  <MenuItem value="custom">Custom redirect URL</MenuItem>
                </Select>
              </FormControl>
              {exitBehavior === 'custom' && (
                <TextField
                  label="Custom Redirect URL"
                  value={customExitUrl}
                  onChange={(e) => setCustomExitUrl(e.target.value)}
                  placeholder="https://..."
                  size="small"
                  fullWidth
                  sx={{ mt: 2 }}
                />
              )}
            </PanelSection>

            {/* Form Theming (P3 - Issue #471) */}
            <PanelSection title="Form Appearance">
              <FormControlLabel
                control={<Switch checked={showThemePanel} onChange={(e) => setShowThemePanel(e.target.checked)} />}
                label="Customize form appearance"
              />
              <Collapse in={showThemePanel}>
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: 1, minWidth: 120 }}>
                      <Typography
                        variant="caption"
                        gutterBottom
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        Primary Color
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <input
                          type="color"
                          value={formTheme.primaryColor || baseColors.info[500]}
                          onChange={(e) => setFormTheme({ ...formTheme, primaryColor: e.target.value })}
                          style={{ width: 48, height: 32, border: 'none', cursor: 'pointer' }}
                        />
                        <TextField
                          size="small"
                          value={formTheme.primaryColor || baseColors.info[500]}
                          onChange={(e) => setFormTheme({ ...formTheme, primaryColor: e.target.value })}
                          sx={{ width: 100 }}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 120 }}>
                      <Typography
                        variant="caption"
                        gutterBottom
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        Secondary Color
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <input
                          type="color"
                          value={formTheme.secondaryColor || baseColors.warning[500]}
                          onChange={(e) => setFormTheme({ ...formTheme, secondaryColor: e.target.value })}
                          style={{ width: 48, height: 32, border: 'none', cursor: 'pointer' }}
                        />
                        <TextField
                          size="small"
                          value={formTheme.secondaryColor || baseColors.warning[500]}
                          onChange={(e) => setFormTheme({ ...formTheme, secondaryColor: e.target.value })}
                          sx={{ width: 100 }}
                        />
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: 1, minWidth: 120 }}>
                      <Typography
                        variant="caption"
                        gutterBottom
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        Background Color
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <input
                          type="color"
                          value={formTheme.backgroundColor || baseColors.neutral[0]}
                          onChange={(e) => setFormTheme({ ...formTheme, backgroundColor: e.target.value })}
                          style={{ width: 48, height: 32, border: 'none', cursor: 'pointer' }}
                        />
                        <TextField
                          size="small"
                          value={formTheme.backgroundColor || baseColors.neutral[0]}
                          onChange={(e) => setFormTheme({ ...formTheme, backgroundColor: e.target.value })}
                          sx={{ width: 100 }}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 120 }}>
                      <Typography
                        variant="caption"
                        gutterBottom
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        Text Color
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <input
                          type="color"
                          value={formTheme.textColor || baseColors.neutral[900]}
                          onChange={(e) => setFormTheme({ ...formTheme, textColor: e.target.value })}
                          style={{ width: 48, height: 32, border: 'none', cursor: 'pointer' }}
                        />
                        <TextField
                          size="small"
                          value={formTheme.textColor || baseColors.neutral[900]}
                          onChange={(e) => setFormTheme({ ...formTheme, textColor: e.target.value })}
                          sx={{ width: 100 }}
                        />
                      </Box>
                    </Box>
                  </Box>

                  <TextField
                    label="Font Family"
                    size="small"
                    value={formTheme.fontFamily || 'Inter, sans-serif'}
                    onChange={(e) => setFormTheme({ ...formTheme, fontFamily: e.target.value })}
                    placeholder="Inter, sans-serif"
                    fullWidth
                  />

                  <Box>
                    <Typography
                      variant="caption"
                      gutterBottom
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      Border Radius: {formTheme.borderRadius || 8}px
                    </Typography>
                    <Slider
                      value={formTheme.borderRadius || 8}
                      onChange={(_, value) => setFormTheme({ ...formTheme, borderRadius: value as number })}
                      min={0}
                      max={24}
                      step={2}
                      valueLabelDisplay="auto"
                      marks={[
                        { value: 0, label: '0' },
                        { value: 8, label: '8' },
                        { value: 16, label: '16' },
                        { value: 24, label: '24' },
                      ]}
                    />
                  </Box>

                  <TextField
                    label="Logo URL"
                    size="small"
                    value={formTheme.logoUrl || ''}
                    onChange={(e) => setFormTheme({ ...formTheme, logoUrl: e.target.value })}
                    placeholder="https://your-logo.png"
                    fullWidth
                  />

                  <FormControl size="small" fullWidth>
                    <InputLabel>Logo Position</InputLabel>
                    <Select
                      value={formTheme.logoPosition || 'left'}
                      onChange={(e) =>
                        setFormTheme({ ...formTheme, logoPosition: e.target.value as 'left' | 'center' | 'right' })
                      }
                      label="Logo Position"
                      MenuProps={{
                        slotProps: {
                          backdrop: { invisible: true },
                        },
                      }}
                    >
                      <MenuItem value="left">Left</MenuItem>
                      <MenuItem value="center">Center</MenuItem>
                      <MenuItem value="right">Right</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={formTheme.supportsDarkMode ?? true}
                        onChange={(e) => setFormTheme({ ...formTheme, supportsDarkMode: e.target.checked })}
                      />
                    }
                    label="Support dark mode"
                  />

                  <TextField
                    label="Custom CSS"
                    size="small"
                    value={formTheme.customCss || ''}
                    onChange={(e) => setFormTheme({ ...formTheme, customCss: e.target.value })}
                    placeholder=".my-form { ... }"
                    multiline
                    rows={3}
                    fullWidth
                  />
                </Box>
              </Collapse>
            </PanelSection>

            <PanelSection title="Rate Calculator">
              <FormControlLabel
                control={
                  <Switch
                    checked={rateCalculatorEnabled}
                    onChange={(e) => setRateCalculatorEnabled(e.target.checked)}
                  />
                }
                label="Enable rate calculator panel"
              />
              {rateCalculatorEnabled && (
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      Max LTV: {rateCalculatorConfig.maxLTV}%
                    </Typography>
                    <Slider
                      value={rateCalculatorConfig.maxLTV}
                      onChange={(_, v) => setRateCalculatorConfig({ ...rateCalculatorConfig, maxLTV: v as number })}
                      min={50}
                      max={100}
                      step={5}
                      valueLabelDisplay="auto"
                      aria-label="Maximum LTV percentage"
                    />
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      Max LTC: {rateCalculatorConfig.maxLTC}%
                    </Typography>
                    <Slider
                      value={rateCalculatorConfig.maxLTC}
                      onChange={(_, v) => setRateCalculatorConfig({ ...rateCalculatorConfig, maxLTC: v as number })}
                      min={50}
                      max={100}
                      step={5}
                      valueLabelDisplay="auto"
                      aria-label="Maximum LTC percentage"
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      label="Min Loan Amount"
                      type="number"
                      value={rateCalculatorConfig.minLoanAmount}
                      onChange={(e) =>
                        setRateCalculatorConfig({ ...rateCalculatorConfig, minLoanAmount: Number(e.target.value) })
                      }
                      size="small"
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="Max Loan Amount"
                      type="number"
                      value={rateCalculatorConfig.maxLoanAmount}
                      onChange={(e) =>
                        setRateCalculatorConfig({ ...rateCalculatorConfig, maxLoanAmount: Number(e.target.value) })
                      }
                      size="small"
                      sx={{ flex: 1 }}
                    />
                  </Box>
                  <TextField
                    label="Default Rate (%)"
                    type="number"
                    value={rateCalculatorConfig.defaultRate}
                    onChange={(e) =>
                      setRateCalculatorConfig({ ...rateCalculatorConfig, defaultRate: Number(e.target.value) })
                    }
                    size="small"
                    slotProps={{
                      htmlInput: { step: 0.25 },
                    }}
                  />
                </Box>
              )}
            </PanelSection>

            {/* Delete section for custom templates */}
            {canDelete && (
              <PanelSection title="Danger Zone">
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Deleting this template cannot be undone. Any application links using this template will stop working.
                </Alert>
                <GlassButton
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={onDelete}
                  loading={isDeleting}
                  disabled={isSaving}
                >
                  Delete Template
                </GlassButton>
              </PanelSection>
            )}
          </Box>
        )}

        {/* Compliance Tab */}
        {activeTab === 3 && (
          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            role="tabpanel"
            id="tabpanel-compliance"
            aria-labelledby="tab-compliance"
          >
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mb: 1,
              }}
            >
              Compliance questions are shown at the end of the intake wizard and must be answered before submission.
            </Typography>

            {complianceQuestions.map((question, index) => (
              <Box
                key={question.id}
                sx={{
                  p: 2,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Chip label={index + 1} size="small" sx={{ mt: 0.5 }} />
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <TextField
                      label="Question"
                      value={question.question}
                      onChange={(e) => handleUpdateComplianceQuestion(question.id, { question: e.target.value })}
                      size="small"
                      fullWidth
                      multiline
                      rows={2}
                      aria-label={`Compliance question ${index + 1}`}
                    />
                    <TextField
                      label="Blocking Message"
                      value={question.blockingMessage || ''}
                      onChange={(e) => handleUpdateComplianceQuestion(question.id, { blockingMessage: e.target.value })}
                      size="small"
                      fullWidth
                      placeholder="Message shown if user doesn't acknowledge"
                      helperText="Shown when user doesn't accept this requirement"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={question.requiredAnswer}
                          onChange={(e) =>
                            handleUpdateComplianceQuestion(question.id, { requiredAnswer: e.target.checked })
                          }
                          size="small"
                        />
                      }
                      label="Must answer Yes to proceed"
                    />
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveComplianceQuestion(question.id)}
                    color="error"
                    aria-label={`Delete compliance question ${index + 1}`}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
            ))}

            <GlassButton variant="outlined" startIcon={<AddIcon />} onClick={handleAddComplianceQuestion} fullWidth>
              Add Compliance Question
            </GlassButton>

            {complianceQuestions.length === 0 && (
              <Alert severity="info">
                No compliance questions added. Add questions to ensure borrowers acknowledge important terms.
              </Alert>
            )}
          </Box>
        )}
      </SplitPanelContent>
      <SplitPanelFooter>
        <GlassButton variant="outlined" onClick={onClose} disabled={isSaving || isDeleting}>
          {t('common.cancel') || 'Cancel'}
        </GlassButton>
        <Box sx={{ flex: 1 }} />
        {onPreview && (
          <GlassButton
            variant="outlined"
            startIcon={<PreviewIcon />}
            onClick={handlePreview}
            disabled={isSaving || isDeleting}
          >
            Preview
          </GlassButton>
        )}
        <GlassButton
          variant="gradient"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          loading={isSaving}
          disabled={isDeleting}
        >
          {isSystemTemplate ? 'Create Custom Copy' : t('common.save') || 'Save'}
        </GlassButton>
      </SplitPanelFooter>
    </DetailPanel>
  );
}

export default IntakeTemplateEditor;
